/* Scenario configs — generated by sales-academy-factory. One primary scenario
 * per persona drives the role-play screen + the temperature gauge. */
import type { PersonaKey } from '@/constants/avatars';
import type { ScenarioConfig } from '@/types/scenario';

export const SCENARIO_DAN: ScenarioConfig = {
  scenario_id: 'scn-dan',
  persona_id: 'dan',
  display_name: 'Dan Kowalski',
  short_label: "The Buyer Who Leads with 'Your Competitor Charges Less'",
  context_line:
    "A skeptical price-shopping buyer opens by demanding a commission reduction and won't engage on representation value until the agent proves they understand his specific search criteria and can back every recommendation with market data.",
  mood_tags: ['Time-boxed', 'Skeptical', 'Numbers-fluent'],
  start_temperature: 2,
  milestones: [
    'conduct_needs_assessment_before_defending_fee',
    'provide_data_backed_value_justification',
    'disclose_commission_structure_in_plain_language',
  ],
  core_objections: [
    "Every agent I've talked to says they 'go above and beyond' — that's not an answer, that's a slogan. What specifically do you do that the two-percent guy on Zillow doesn't?",
    "I've bought property before. I know how this works. You plug my criteria into the MLS, you book showings, you write up the offer I tell you to write. That's not worth three percent of a three-hundred-forty-thousand-dollar house.",
    "Look, I'm not trying to be a jerk about it. I'm self-employed — every dollar of margin matters to me. If you can't sharpen your pencil at all, I've got two other agents who will.",
    "My last agent told me the inspection item was 'minor and typical for a home this age.' It was a fourteen-thousand-dollar sewer line. So forgive me if I'm not impressed by the word 'experienced.'",
  ],
  unlock_requires: 'always',
  opening_line:
    "Hi — I'm Dan. Every agent I've talked to says they 'go above and beyond' — that's not an answer, that's a slogan. What specifically do you do that the two-percent guy on Zillow doesn't?",
};

export const SCENARIO_RITA: ScenarioConfig = {
  scenario_id: 'scn-rita',
  persona_id: 'rita',
  display_name: 'Rita Solano',
  short_label: 'The Seller Who Was Overpriced and Abandoned by Her Last Agent',
  context_line:
    'A hostile seller who had a previous listing expire after her former agent inflated the price demands accountability and a concrete re-pricing plan before she will consider signing a new listing agreement.',
  mood_tags: ['Hostile', 'Burned', 'Guarded'],
  start_temperature: 4,
  milestones: [
    'acknowledge_prior_harm_without_deflecting',
    'present_comparable_sales_data_for_accurate_pricing',
    'commit_to_specific_communication_and_marketing_plan',
  ],
  core_objections: [
    'Marcus told me the exact same thing about being different. What I need is not a promise — I need you to show me the math on paper, line by line, before I sign anything.',
    "Six months. Two showings. One offer I didn't even know about until I went looking for it. So when you say 'full communication,' I'm going to need you to define exactly what that means in writing.",
    "You're quoting me $577,000. Marcus quoted me $624,000 and said he had the comps to back it up. How do I know your number isn't just the opposite kind of manipulation — pricing low to get a fast sale and a fast commission?",
    "I've already lost six months and I have a deposit in Sarasota that expires in 14 weeks. If you waste my time with a strategy that doesn't work, I don't get a do-over. So I'm going to ask you to be very careful about what you promise me today.",
  ],
  unlock_requires: 'module:1:complete',
  opening_line:
    "Hi — I'm Rita. Marcus told me the exact same thing about being different. What I need is not a promise — I need you to show me the math on paper, line by line, before I sign anything.",
};

export const SCENARIO_MAYA: ScenarioConfig = {
  scenario_id: 'scn-maya',
  persona_id: 'maya',
  display_name: 'Maya Osei',
  short_label: 'The Emotionally Committed Buyer Whose Budget Cannot Support the Dream Home',
  context_line:
    'A vulnerable first-time buyer is emotionally fixated on a property that exceeds her safe financing ceiling, and the rep must resist the temptation to validate the stretch in order to protect her long-term financial wellbeing.',
  mood_tags: ['Trusting', 'At-risk', 'Unhurried'],
  start_temperature: 1,
  milestones: [
    'surface_full_budget_and_long_term_goals_before_recommending',
    'disclose_risk_of_overextension_transparently',
    'demonstrate_restraint_by_declining_to_endorse_unsuitable_property',
  ],
  core_objections: [
    "I know it's technically over my approval amount, but the seller might come down a little, right? And I could ask them to cover closing costs — people do that all the time.",
    "I've been looking for four months and every time I wait, something goes under contract. I just feel like if I don't move now, I'm going to lose this one too.",
    "My mom keeps telling me rent is just throwing money away. I'm already 31 — I feel like I'm behind. I really just need someone to help me get into this house.",
  ],
  unlock_requires: 'module:3:complete',
  opening_line:
    "Hi — I'm Maya. I know it's technically over my approval amount, but the seller might come down a little, right? And I could ask them to cover closing costs — people do that all the time.",
};

export const SCENARIO_TYLER: ScenarioConfig = {
  scenario_id: 'scn-tyler',
  persona_id: 'tyler',
  display_name: 'Tyler Brandt',
  short_label: "The Eager First-Time Buyer Who Doesn't Know What He Doesn't Know",
  context_line:
    'An enthusiastic first-time buyer wants to make an offer on the first home he tours, and the rep must slow the process, surface undisclosed priorities, and explain agency relationships and material disclosures before momentum overrides due diligence.',
  mood_tags: ['Earnest', 'Low-context', 'Curious'],
  start_temperature: 0,
  milestones: [
    'complete_structured_needs_assessment_covering_all_four_domains',
    'explain_agency_relationship_and_buyer_representation_agreement',
    'proactively_raise_material_fact_and_inspection_considerations',
  ],
  core_objections: [
    "I already ran the numbers like five times — $304,900 fits inside my $310K approval, so I'm good, right? Can we just start the paperwork?",
    "I get that there's a process, but honestly I don't want to lose this place over some technicality. Other people were walking through here too.",
    'Do I really need to sign some agreement before you can help me? That sounds like more stuff that slows things down.',
    "The second bedroom is totally fine for an office — I just need a door and a desk, I'm not picky about size.",
  ],
  unlock_requires: 'module:3:complete',
  opening_line:
    "Hi — I'm Tyler. I already ran the numbers like five times — $304,900 fits inside my $310K approval, so I'm good, right? Can we just start the paperwork?",
};

export const SCENARIOS: Record<string, ScenarioConfig> = {
  'scn-dan': SCENARIO_DAN,
  'scn-rita': SCENARIO_RITA,
  'scn-maya': SCENARIO_MAYA,
  'scn-tyler': SCENARIO_TYLER,
};

export const SCENARIOS_BY_PERSONA: Record<PersonaKey, ScenarioConfig> = {
  dan: SCENARIO_DAN,
  rita: SCENARIO_RITA,
  maya: SCENARIO_MAYA,
  tyler: SCENARIO_TYLER,
};

export const SCENARIO_ORDER = ['scn-dan', 'scn-rita', 'scn-maya', 'scn-tyler'];
